import {
  Box,
  FormControlLabel,
  Button,
  Container,
  Grid,
  Paper,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Typography,
} from '@mui/material'
import { getLayerInfoByHarvest } from 'api/field'
import { useReactToPrint } from 'react-to-print'
import { Print as PrintIcon } from '@mui/icons-material'
import { unitConvert } from '@farmtrx/units'
import { useEffect } from 'react'
import { isEmpty } from 'es-toolkit/compat'
import { isLegacySLD, getTranslatedName } from 'common/misc'
import { transform } from 'ol/proj'
import { createMapParams } from 'components/map/wms-layer'
import colors from 'common/colors'
import GeoserverImage from 'components/geoserver-image'
import {
  geoserverUrl,
  harvestWmsParams,
  isGisProxyEnabled,
  wmsEndpointUrl,
} from 'common/geoserver'

import EqualRangeLegend from 'components/layer-legend/equal-range-legend'
import { useSelector } from 'react-redux'

const MAX_HEIGHT = 450
const MAX_WIDTH = 600

export default function HarvestReport(props) {
  function calculateHeightAndWidth(extentMin, extentMax) {
    const x = Math.abs(extentMax[0] - extentMin[0])
    const y = Math.abs(extentMax[1] - extentMin[1])

    let tmpHeight = Math.floor((y / x) * MAX_WIDTH)
    if (tmpHeight > MAX_HEIGHT) {
      setHeight(MAX_HEIGHT)
      setWidth(Math.floor((x / y) * MAX_HEIGHT))
    } else {
      setHeight(tmpHeight)
      setWidth(MAX_WIDTH)
    }
  }

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const layerInfo = useSelector((state) => state.field['layerInfo'])
  const hardware = useSelector((state) => state.hardware.collection)
  const equipment = useSelector((state) => state.equipment.collection)
  const userPrefs = useSelector((state) => state.account.singleton.prefs)
  const crops = useSelector((state) => state.crop.cropConstantsCollection)

  const [url, setMapUrl] = useState('')

  const [legend, setLegend] = useState(null)
  const [height, setHeight] = useState(450)
  const [width, setWidth] = useState(450)
  const [bbox, setBbox] = useState([])
  const [legendSLD, setLegendSLD] = useState()
  const [cqlFilter, setCqlFilter] = useState()

  const [equipmentInfo, setEquipmentInfo] = useState([])

  const componentRef = useRef()
  const selectedHarvest = props.harvestObj
  const selectedLayerObj = props.layerName
  const swathSldExtension = props.swathSldExtension
  const swathParameter = props.swathParameter

  useEffect(() => {
    let harvesterInfo = selectedHarvest?.stats?.harvester_info
    let finalArray = []
    if (harvesterInfo) {
      // will work if harvesterInfo is either an array or map
      // (seems the map is keyed by ymId or something that looks like it)
      for (let harvester in harvesterInfo) {
        const h = harvesterInfo[harvester]
        // if harvesterInfo has a ymId use it, if it doesn't then try to use
        // whatever harvester info is indexed by.
        const ymId = h?.ymId || harvester
        let hwObj = hardware.find((hw) => hw?.farmtrxHardwareId === ymId)
        let eqObj =
          hwObj && equipment.find((eq) => eq.id === hwObj?.equipmentId)
        finalArray.push({
          eqNickname: eqObj ? eqObj.nickname : ymId,
          totalYieldG: h['total_yield_g'],
          ymId: ymId,
          perCombineHarvest: h['harvested_area_m2'],
        })
      }
    }
    setEquipmentInfo(finalArray)
  }, [selectedHarvest])

  useEffect(() => {
    //get map
    if (selectedHarvest) {
      let layerDetails = {}
      let legacySld = false
      if (selectedHarvest?.stats?.lp_version) {
        legacySld = isLegacySLD(selectedHarvest?.stats?.lp_version)
      }
      setCqlFilter(encodeURI(`harvest_id='${selectedHarvest.id}'`))
      if (layerInfo && layerInfo.length) {
        layerDetails = layerInfo.find(
          (l) =>
            l.harvestId === selectedHarvest.id &&
            l.layerName === selectedLayerObj.key,
        )
        if (layerDetails && Object.keys(layerDetails).length) {
          let legendUrl = layerDetails.legendUrl
          if (
            legendUrl !== '' &&
            legacySld &&
            !(
              userPrefs?.units?.yieldunit_in == 'bu_ac' ||
              userPrefs?.units?.yieldunit_in == 'lbs_acre'
            )
          ) {
            legendUrl = layerDetails.legendUrl.replace('.sld', '_hct.sld')
          } else if (swathSldExtension) {
            legendUrl = layerDetails.legendUrl.replace(
              '.sld',
              swathSldExtension,
            )
          }

          setLegendSLD(legendUrl)

          const extentMin = [layerDetails.bboxMinx, layerDetails.bboxMiny]
          const extentMax = [layerDetails.bboxMaxx, layerDetails.bboxMaxy]
          const transformedExtentMin = transform(
            extentMin,
            'EPSG:4326',
            'EPSG:3857',
          )
          const transformedExtentMax = transform(
            extentMax,
            'EPSG:4326',
            'EPSG:3857',
          )
          setBbox([
            transformedExtentMin[0],
            transformedExtentMin[1],
            transformedExtentMax[0],
            transformedExtentMax[1],
          ])

          calculateHeightAndWidth(transformedExtentMin, transformedExtentMax)
        }
      } else {
        dispatch(
          getLayerInfoByHarvest(selectedHarvest.id, selectedHarvest.cropYear),
        )
      }
    }
  }, [layerInfo, selectedHarvest, selectedLayerObj, swathSldExtension])

  useEffect(() => {
    if (bbox.length > 0 && Object.keys(selectedLayerObj).length) {
      let layerDetails = {}
      layerDetails = layerInfo.find(
        (l) =>
          l.harvestId === selectedHarvest.id &&
          l.layerName === selectedLayerObj.key,
      )
      if (layerDetails) {
        // GIS proxy URLs, built from the same parameters as the GeoServer
        // ones below.
        const proxyUrl = (wmsParams) =>
          `${wmsEndpointUrl('wms')}?${new URLSearchParams(
            harvestWmsParams(wmsParams, {
              harvestId: selectedHarvest.id,
              legendUrl: layerDetails.legendUrl,
              layerName: selectedLayerObj.key,
            }),
          )}`
        const getMap = {
          SERVICE: 'WMS',
          VERSION: '1.3.0',
          REQUEST: 'GetMap',
          BBOX: bbox.join(','),
          HEIGHT: height,
          WIDTH: width,
          CRS: 'EPSG:3857',
          FORMAT: 'image/png',
        }
        const params = createMapParams(
          layerDetails,
          legendSLD,
          swathParameter,
          true,
        )
        if (params?.env) {
          const crop = crops.find((c) => c.id === selectedHarvest.cropId)
          const customLegendData =
            layerDetails?.customLegends.find(
              (l) => l.parameter === swathParameter,
            ) || layerDetails?.customLegends[0]
          setLegend(
            <EqualRangeLegend
              key={`${layerDetails.name}_minmax`}
              layer={{
                name: layerDetails.layerName,
                translation: layerDetails.layerName + '_map',
              }}
              colors={customLegendData.legendData.ranges.c}
              quantities={customLegendData.legendData.ranges.q}
              numRanges={customLegendData.legendData['num_ranges']}
              cropBuPerTonne={crop?.buPerTonne || 40}
              selectedSwathParameter={swathParameter}
              showUnits={true}
            />,
          )
          setMapUrl(
            isGisProxyEnabled()
              ? proxyUrl({
                  ...getMap,
                  STYLES: params.STYLES,
                  ENV: params.env,
                  LAYERS: params.LAYERS,
                })
              : `${geoserverUrl('/ows')}?service=WMS&version=1.3.0&request=GetMap&bbox=${bbox}&height=${height}&width=${width}&crs=EPSG:3857&CQL_FILTER=${cqlFilter}&STYLES=${params.STYLES}&format=image%2Fpng&env=${encodeURIComponent(params.env)}&LAYERS=${params.LAYERS}`,
          )
        } else {
          setLegend(
            <GeoserverImage
              key={selectedLayerObj.key + '_legend'}
              src={
                isGisProxyEnabled()
                  ? proxyUrl({
                      SERVICE: 'WMS',
                      VERSION: '1.3.0',
                      REQUEST: 'GetLegendGraphic',
                      FORMAT: 'image/png',
                      SCALE: 0,
                      LAYER: selectedLayerObj.key,
                      SLD: legendSLD,
                      LEGEND_OPTIONS:
                        'fontAntiAliasing:true;fontName:SansSerif',
                    })
                  : `${geoserverUrl('/ows')}?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetLegendGraphic&FORMAT=image%2Fpng&SCALE=0&LAYER=${selectedLayerObj.key}&SLD=${legendSLD}&LEGEND_OPTIONS=fontAntiAliasing:true;fontName:SansSerif`
              }
              alt="legend"
              style={{
                width: '150px',
                maxHeight: `${MAX_HEIGHT}px`,
                objectFit: 'contain',
              }}
            />,
          )
          setMapUrl(
            isGisProxyEnabled()
              ? proxyUrl({ ...getMap, SLD: legendSLD, STYLES: '' })
              : `${geoserverUrl('/ows')}?service=WMS&version=1.3.0&request=GetMap&bbox=${bbox}&height=${height}&width=${width}&SLD=${legendSLD}&crs=EPSG:3857&CQL_FILTER=${cqlFilter}&styles=&format=image%2Fpng`,
          )
        }
      }
    }
  }, [
    height,
    width,
    bbox,
    legendSLD,
    cqlFilter,
    selectedLayerObj,
    layerInfo,
    crops,
  ])

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `${selectedHarvest['fieldName']} - ${selectedHarvest['cropName']}`,
  })

  return isEmpty(selectedHarvest) && isEmpty(selectedLayerObj) ? (
    <Box
      sx={{
        display: 'flex',
        m: 2,
        p: 2,
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <b>{t('click_generate_view_report')}</b>
    </Box>
  ) : (
    <Box
      sx={{
        width: 1,
        display: 'flex',
      }}
    >
      <Container
        fixed
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: '1em',
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            my: 2,
          }}
        >
          <Button
            variant="contained"
            startIcon={
              <PrintIcon
                sx={{
                  '& path': {
                    fill: colors.white,
                  },
                }}
              />
            }
            onClick={() => handlePrint()}
          >
            {t('print_report')}
          </Button>
        </Box>
        <Box
          sx={{ border: `5px solid ${colors.brand}`, overflowY: 'scroll' }}
          ref={componentRef}
        >
          <h2 style={{ marginLeft: '16px' }}>
            {`${t('harvest_report')}`.toUpperCase()}
          </h2>
          <>
            <Box
              sx={{
                mx: 2,
                my: 2,
              }}
            >
              <Grid container spacing={{ xs: 0.5, md: 0.5 }}>
                <Grid size={5}>
                  <FormControlLabel
                    control={<span>{selectedHarvest?.stats?.field_name}</span>}
                    label={`${t('field_name')}:`}
                    labelPlacement="start"
                  />
                </Grid>
                <Grid size={7}></Grid>
                <Grid size={5}>
                  <FormControlLabel
                    control={
                      <span>
                        {selectedHarvest &&
                          Object.keys(selectedHarvest).length &&
                          getTranslatedName(
                            selectedHarvest?.crop?.cropClassification,
                          )}
                      </span>
                    }
                    label={`${t('crop_type')}:`}
                    labelPlacement="start"
                  />
                </Grid>
                <Grid size={2}></Grid>
                <Grid size={5}>
                  <FormControlLabel
                    control={<span>{selectedHarvest['cropYear']}</span>}
                    label={`${t('harvest_year')}:`}
                    labelPlacement="start"
                  />
                </Grid>
              </Grid>
            </Box>
            <Box
              sx={{
                mx: 2,
                my: 2,
                minHeight: '300px',
                maxHeight: `${MAX_HEIGHT + 2}px`,
                border: `1px solid ${colors.brand}`,
                borderRadius: '5px',
                textAlign: 'center',
              }}
            >
              <Grid
                container
                spacing={0.5}
                sx={{
                  paddingTop: '5px',
                }}
              >
                <Grid size={9}>
                  {url && (
                    <GeoserverImage
                      key={selectedLayerObj.key}
                      src={url}
                      alt="map"
                      style={{
                        maxWidth: '600px',
                        maxHeight: `${MAX_HEIGHT}px`,
                        objectFit: 'contain',
                      }}
                    />
                  )}
                </Grid>
                <Grid size={3}>{legend}</Grid>
              </Grid>
            </Box>
            <Box
              sx={{
                mx: 2,
                my: 2,
              }}
            >
              <Typography variant="h6">{`${t(
                'agronomic_layer_data',
              )}`}</Typography>
              <hr />
              <Grid container spacing={{ xs: 0.5, md: 0.5 }}>
                <Grid size={5}>
                  <FormControlLabel
                    control={
                      <span>
                        {selectedHarvest &&
                          Object.keys(selectedHarvest).length &&
                          getTranslatedName(selectedHarvest.cropName)}
                      </span>
                    }
                    label={`${t('crop')}:`}
                    labelPlacement="start"
                  />
                </Grid>
                <Grid size={2}></Grid>
                <Grid size={5}>
                  <FormControlLabel
                    control={
                      <span>
                        {selectedHarvest &&
                          Object.keys(selectedHarvest).length &&
                          `${unitConvert
                            .convertYieldToPref(
                              selectedHarvest?.stats?.total_yield_g,
                              userPrefs['units']['totalyield_in'],
                              selectedHarvest?.crop?.buPerTonne,
                            )
                            .toFixed(2)} ${t(
                            userPrefs['units']['totalyield_in'],
                          )}`}
                      </span>
                    }
                    label={`${t('total_yield')}:`}
                    labelPlacement="start"
                  />
                </Grid>
                <Grid size={5}>
                  <FormControlLabel
                    control={
                      <span>
                        {selectedHarvest.stats?.avg_moist
                          ? `${selectedHarvest.stats?.avg_moist?.toFixed(2)} %`
                          : t('n_a')}
                      </span>
                    }
                    label={`${t('harvest_moisture')}:`}
                    labelPlacement="start"
                  />
                </Grid>
                <Grid size={2}></Grid>
                <Grid size={5}>
                  <FormControlLabel
                    control={
                      <span>
                        {selectedHarvest &&
                          Object.keys(selectedHarvest).length &&
                          `${unitConvert
                            .convertYieldToPref(
                              selectedHarvest?.stats?.avg_harvested_yield_g_m2,
                              userPrefs['units']['yieldunit_in'],
                              selectedHarvest?.crop?.buPerTonne,
                            )
                            .toFixed(2)} ${t(
                            userPrefs['units']['yieldunit_in'],
                          )}`}
                      </span>
                    }
                    label={`${t('harvested_average_yield')}:`}
                    labelPlacement="start"
                  />
                </Grid>
                <Grid size={5}>
                  <FormControlLabel
                    control={
                      <span>
                        {selectedHarvest &&
                          Object.keys(selectedHarvest).length &&
                          `${unitConvert
                            .convertAreaToPref(
                              selectedHarvest?.stats?.harvested_area_m2,
                              userPrefs['units']['area_in'],
                            )
                            .toFixed(2)} ${t(userPrefs['units']['area_in'])}`}
                      </span>
                    }
                    label={`${t('harvested_area')}:`}
                    labelPlacement="start"
                  />
                </Grid>
                <Grid size={2}></Grid>
                <Grid size={5}>
                  <FormControlLabel
                    control={<span>{selectedLayerObj['name']}</span>}
                    label={`${t('map_type')}:`}
                    labelPlacement="start"
                  />
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                mx: 2,
                my: 2,
              }}
            >
              <Box>
                <Typography variant="h6">{`${t('field')} / ${t(
                  'equipment',
                )} ${t('data')}`}</Typography>
                <hr />
                <TableContainer component={Paper}>
                  <Table aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('name')}</TableCell>
                        <TableCell align="right">
                          {t('total_per_combine')}
                        </TableCell>
                        <TableCell align="right">
                          {t('harvested_area_per_combine')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {equipmentInfo.map((row) => (
                        <TableRow
                          key={row.ymId}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {row.eqNickname}
                          </TableCell>
                          <TableCell align="right">{`${unitConvert
                            .convertYieldToPref(
                              row.totalYieldG,
                              userPrefs['units']['totalyield_in'],
                              selectedHarvest?.crop?.buPerTonne,
                            )
                            .toFixed(2)} ${t(
                            userPrefs['units']['totalyield_in'],
                          )}`}</TableCell>
                          <TableCell align="right">{`${unitConvert
                            .convertAreaToPref(
                              row.perCombineHarvest,
                              userPrefs['units']['area_in'],
                            )
                            .toFixed(2)} ${t(
                            userPrefs['units']['area_in'],
                          )}`}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          </>
        </Box>
      </Container>
    </Box>
  )
}

HarvestReport.displayName = 'HarvestReport'
